import { ComponentState, FunctionComponent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { SmoMoonList } from '../../../../Models/Smo/SmoMoonList';
import UserStoreActions from '../../../../Modules/Redux/Global/User/UserStoreActions';
import UserStoreSelector from '../../../../Modules/Redux/Global/User/UserStoreSelector';
import CreateMoonListActions from '../../../../Modules/Redux/Pages/CreateMoonList/CreateMoonListActions';
import MoonListActions from '../../../../Modules/Redux/Pages/MoonList/MoonListActions';
import MoonListSelectors from '../../../../Modules/Redux/Pages/MoonList/MoonListSelectors';
import { useConfirm } from '../../../../Utils/Hooks/UseConfirm';
import { Optional } from '../../../../Utils/Types';
import { Box } from '../../Components/Box/Box';
import { CenterContent } from '../../Components/CenterContent/CenterContent';
import { Grid } from '../../Components/Grid/Grid';
import { LoadingIndicator } from '../../Components/LoadingIndicator/LoadingIndicator';
import { MoonCell } from '../../Components/MoonCell/MoonCell';
import { NavigationBar } from '../../Components/NavigationBar/NavigationBar';
import { PageLayout } from '../../Components/PageLayout/PageLayout';
import { PrimaryButton } from '../../Components/PrimaryButton/PrimaryButton';
import { Switch } from '../../Components/Switch/Switch';
import { SwitchCase } from '../../Components/SwitchCase/SwitchCase';
import { TabBar } from '../../Components/TabBar/TabBar';
import { TabItem } from '../../Components/TabItem/TabItem';
import { Tag } from '../../Components/Tag/Tag';

export const MoonListPage: FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const dispatch = useDispatch();
  const moonList: Optional<SmoMoonList> = useSelector(MoonListSelectors.moonList);
  const isOwner: boolean = useSelector(MoonListSelectors.isOwner);
  const isFavorite: boolean = useSelector(UserStoreSelector.isFavoriteMoonList(id ?? ''));
  const state: ComponentState = useSelector(MoonListSelectors.state);

  useEffect(() => {
    if (id) {
      dispatch(MoonListActions.getMoonListWithId(id));
    }
    return (): void => {
      dispatch(MoonListActions.resetState());
    };
  }, [id]);

  useEffect(() => {
    switch (state.current) {
      case 'run/success':
        navigate('/stream');
        break;
      case 'delete/success':
        navigate('/moon/list');
        break;
    }
  }, [state]);

  function startRun(): void {
    if (!id) {
      return;
    }
    dispatch(MoonListActions.startRunWithMoonListId(id));
  }

  function deleteMoonList(): void {
    confirm.show('Do you want to delete this moon list?', (hasConfirmed) => {
      if (hasConfirmed && id) {
        dispatch(MoonListActions.deleteMoonListWithId(id));
      }
    });
  }

  function duplicate(): void {
    if (moonList) {
      dispatch(CreateMoonListActions.createFromMoonList(moonList));
      navigate('/moon/list/create');
    }
  }

  function favorite(): void {
    if (!id) {
      return;
    }
    dispatch(UserStoreActions.favoriteMoonListWithId(id));
  }

  return (
    <Switch value={state.current}>
      <SwitchCase value="loading">
        <CenterContent className="w-full h-screen">
          <Box>
            <div className="p-4">
              <LoadingIndicator />
            </div>
          </Box>
        </CenterContent>
      </SwitchCase>

      <SwitchCase value="*">
        {moonList ? (
          <div className="relative pb-8">
            <NavigationBar style="translucent" position="absolute">
              <TabBar>
                {isOwner ? (
                  <TabItem title="Delete" icon="/images/icons/trash_red.svg" onClick={deleteMoonList} />
                ) : undefined}
                <TabItem
                  title="Duplicate"
                  icon="/images/icons/duplicate_red.svg"
                  tooltip="Create and edit a new moon list from this list."
                  onClick={duplicate}
                />
                <TabItem
                  title={isFavorite ? 'Remove favorite' : 'Favorite'}
                  icon={isFavorite ? '/images/icons/star_fill_red.svg' : '/images/icons/star_red.svg'}
                  onClick={favorite}
                />
              </TabBar>
            </NavigationBar>

            <div className="relative">
              <img
                className="object-cover object-bottom w-full"
                style={{ height: '25vmax' }}
                src="/images/widget_backgrounds/background_negative.png"
              />
              <div className="absolute bottom-0 left-0 right-0 py-4 bg-gradient-to-t from-bg to-bg-gradient">
                <PageLayout>
                  <div className="items-center justify-between space-x-4 h-stack">
                    <h1 className="large-title">{moonList.name}</h1>
                    <PrimaryButton title="Start a run" onClick={startRun} />
                  </div>
                </PageLayout>
              </div>
            </div>

            <PageLayout>
              <div className="space-y-4">
                <hr />
                <div className="flex-wrap items-center space-x-2 h-stack">
                  <Tag icon="/images/icons/person_fill_red.svg" data={moonList.author.login} />
                  <Tag icon="/images/icons/moon_red.svg" data={`${moonList.moonCount}`} />
                </div>
              </div>
            </PageLayout>

            <PageLayout>
              <div className="pt-8">
                <Grid fillStyle="auto-fill">
                  {moonList.moons.map((moon, index) => (
                    <MoonCell key={moon.id} moon={moon} index={index + 1} />
                  ))}
                </Grid>
              </div>
            </PageLayout>
          </div>
        ) : undefined}
      </SwitchCase>
    </Switch>
  );
};
